import React, { useState } from 'react';
import { MapContainer, TileLayer, Polygon, Circle, useMapEvents, Marker, Popup } from 'react-leaflet';
import { LatLng } from 'leaflet';
import { 
  MapPin, 
  Home, 
  DollarSign, 
  Waves, 
  Heart, 
  Bed, 
  Car,
  Wifi,
  Search,
  X,
  Square,
  Circle as CircleIcon,
  Layers,
  ShoppingBag,
  GraduationCap,
  Plane
} from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import './LeafletConfig';

interface PropertyRequirements {
  budgetMin: number;
  budgetMax: number;
  bedrooms: number;
  hasPool: boolean;
  allowsPets: boolean;
  hasParking: boolean;
  hasWifi: boolean;
  additionalRequirements: string;
}

interface ServiceArea {
  id: string;
  name: string;
  type: 'neighborhood' | 'university' | 'shopping' | 'airport';
  center?: [number, number];
  radius?: number;
  polygon?: [number, number][];
  color: string;
  description: string;
}

const PropertySearch: React.FC = () => {
  const [searchArea, setSearchArea] = useState<LatLng[]>([]);
  const [drawingMode, setDrawingMode] = useState<'none' | 'polygon' | 'circle'>('none');
  const [circleCenter, setCircleCenter] = useState<LatLng | null>(null);
  const [circleRadius, setCircleRadius] = useState<number>(1000);
  const [showForm, setShowForm] = useState(false);
  const [showServiceAreas, setShowServiceAreas] = useState(true);
  const [selectedPresetArea, setSelectedPresetArea] = useState<string | null>(null);
  const [requirements, setRequirements] = useState<PropertyRequirements>({
    budgetMin: 10000,
    budgetMax: 50000,
    bedrooms: 1,
    hasPool: false,
    allowsPets: false,
    hasParking: false,
    hasWifi: true,
    additionalRequirements: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Chiang Mai center coordinates (accurate city center)
  const chiangMaiCenter: [number, number] = [18.7883, 98.9853];

  // Predefined service areas and neighborhoods (corrected coordinates)
  const serviceAreas: ServiceArea[] = [
    {
      id: 'old-city',
      name: 'Old City',
      type: 'neighborhood',
      center: [18.7879, 98.9900],
      polygon: [
        [18.7955, 98.9844],
        [18.7955, 98.9956],
        [18.7803, 98.9956],
        [18.7803, 98.9844],
        [18.7803, 98.9844]
      ],
      color: '#10B981',
      description: 'Historic center with temples and traditional markets'
    },
    {
      id: 'nimman',
      name: 'Nimman Area', 
      type: 'neighborhood',
      center: [18.8015, 98.9520],
      radius: 1000,
      color: '#8B5CF6',
      description: 'Trendy area with cafes, shopping, and nightlife'
    },
    {
      id: 'santitham',
      name: 'Santitham Area',
      type: 'neighborhood',
      center: [18.8050, 98.9750],
      radius: 800,
      color: '#06B6D4',
      description: 'Local neighborhood with authentic restaurants and markets'
    },
    {
      id: 'cmu',
      name: 'Chiang Mai University Area',
      type: 'university',
      center: [18.8030, 98.9490],
      radius: 1500,
      color: '#F59E0B',
      description: 'University district with student housing and amenities'
    },
    {
      id: 'maya-mall',
      name: 'Maya Mall Area',
      type: 'shopping',
      center: [18.8026, 98.9544],
      radius: 500,
      color: '#EF4444',
      description: 'Major shopping and entertainment hub in Nimman'
    },
    {
      id: 'central-festival',
      name: 'Central Festival Area',
      type: 'shopping',
      center: [18.8028, 98.9708],
      radius: 600,
      color: '#EF4444',
      description: 'Large shopping center with restaurants and cinema'
    },
    {
      id: 'airport-zone',
      name: 'Airport Area',
      type: 'airport',
      center: [18.7669, 99.0028],
      radius: 2500,
      color: '#6B7280',
      description: 'Close to Chiang Mai International Airport'
    },
    {
      id: 'hang-dong',
      name: 'Hang Dong District',
      type: 'neighborhood',
      center: [18.6700, 98.9400],
      radius: 2000,
      color: '#059669',
      description: 'Suburban area with larger homes and green spaces'
    },
    {
      id: 'doi-suthep',
      name: 'Doi Suthep Area',
      type: 'neighborhood',
      center: [18.8100, 98.9300],
      radius: 1800,
      color: '#7C3AED',
      description: 'Mountain area with scenic views and cooler climate'
    },
    {
      id: 'chang-khlan',
      name: 'Chang Khlan (Night Bazaar)',
      type: 'shopping',
      center: [18.7870, 98.9920],
      radius: 400,
      color: '#DC2626',
      description: 'Night market and tourist area near the river'
    }
  ];

  const MapEvents = () => {
    useMapEvents({
      click: (e) => {
        if (drawingMode === 'polygon') {
          setSearchArea(prev => [...prev, e.latlng]);
        } else if (drawingMode === 'circle') {
          setCircleCenter(e.latlng);
          setDrawingMode('none');
          setShowForm(true);
        }
      }
    });
    return null;
  };

  const startDrawingPolygon = () => {
    setSearchArea([]);
    setCircleCenter(null);
    setSelectedPresetArea(null);
    setDrawingMode('polygon');
  };

  const startDrawingCircle = () => {
    setSearchArea([]);
    setCircleCenter(null);
    setSelectedPresetArea(null);
    setDrawingMode('circle');
  };

  const finishPolygonDrawing = () => {
    setDrawingMode('none');
    if (searchArea.length >= 3) {
      setShowForm(true);
    }
  };

  const selectPresetArea = (areaId: string) => {
    setSearchArea([]);
    setCircleCenter(null);
    setDrawingMode('none');
    setSelectedPresetArea(areaId);
    setShowForm(true);
  };

  const clearSelection = () => {
    setSearchArea([]);
    setCircleCenter(null);
    setSelectedPresetArea(null);
    setDrawingMode('none');
    setShowForm(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prepare area description for email
    let areaDescription = '';
    if (selectedPresetArea) {
      const area = serviceAreas.find(area => area.id === selectedPresetArea);
      areaDescription = `Preset Area: ${area?.name} - ${area?.description}`;
    } else if (circleCenter) {
      areaDescription = `Custom Circle Area: Center at coordinates (${circleCenter.lat.toFixed(6)}, ${circleCenter.lng.toFixed(6)}) with radius ${circleRadius}m`;
    } else if (searchArea.length >= 3) {
      const coordinates = searchArea.map(point => `(${point.lat.toFixed(6)}, ${point.lng.toFixed(6)})`).join(', ');
      areaDescription = `Custom Polygon Area: ${coordinates}`;
    }

    // Prepare amenities list
    const selectedAmenities = [];
    if (requirements.hasPool) selectedAmenities.push('Swimming Pool');
    if (requirements.allowsPets) selectedAmenities.push('Pet-Friendly');
    if (requirements.hasParking) selectedAmenities.push('Parking Space');
    if (requirements.hasWifi) selectedAmenities.push('WiFi/Internet');

    // Create email content
    const emailSubject = 'Property Search Request - Chiang Mai';
    const emailBody = `Hello Pattama,

I would like to request help finding a property in Chiang Mai with the following specifications:

SEARCH AREA:
${areaDescription}

REQUIREMENTS:
• Budget Range: ฿${requirements.budgetMin.toLocaleString()} - ฿${requirements.budgetMax.toLocaleString()} THB per month
• Number of Bedrooms: ${requirements.bedrooms} bedroom${requirements.bedrooms > 1 ? 's' : ''}
• Desired Amenities: ${selectedAmenities.length > 0 ? selectedAmenities.join(', ') : 'None specified'}

ADDITIONAL REQUIREMENTS:
${requirements.additionalRequirements || 'None specified'}

Please help me find properties that match these criteria. I look forward to hearing from you.

Best regards`;

    // Create mailto link
    const mailtoLink = `mailto:hi@pattama.co?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // For future enhancement: Could integrate with backend API to send emails directly
    // fetch('/api/property-search', { method: 'POST', body: JSON.stringify(searchData) })
    
    // Show success message
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setShowForm(false);
      clearSelection();
    }, 3000);
  };

  const updateRequirement = (key: keyof PropertyRequirements, value: any) => {
    setRequirements(prev => ({ ...prev, [key]: value }));
  };

  const getAreaTypeIcon = (type: string) => {
    switch (type) {
      case 'neighborhood': return Home;
      case 'university': return GraduationCap;
      case 'shopping': return ShoppingBag;
      case 'airport': return Plane;
      default: return MapPin;
    }
  };

  return (
    <section id="property-search" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Find Your Perfect Home in Chiang Mai
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Use our interactive map to define your preferred area and see available services. 
            Choose from preset areas or draw your own custom search zone.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Map Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <MapPin className="mr-2 text-primary-600" size={20} />
                    Select Your Search Area
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowServiceAreas(!showServiceAreas)}
                      className={`px-3 py-2 rounded-lg text-sm flex items-center transition-colors ${
                        showServiceAreas 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Layers size={16} className="mr-1" />
                      Service Areas
                    </button>
                  </div>
                </div>

                {/* Drawing Tools */}
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {drawingMode === 'none' && !circleCenter && searchArea.length === 0 && !selectedPresetArea && (
                      <>
                        <button
                          onClick={startDrawingCircle}
                          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center text-sm font-medium"
                        >
                          <CircleIcon size={16} className="mr-1" />
                          Draw Circle Area
                        </button>
                        <button
                          onClick={startDrawingPolygon}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center text-sm font-medium"
                        >
                          <Square size={16} className="mr-1" />
                          Draw Custom Area
                        </button>
                      </>
                    )}
                    {(searchArea.length > 0 || circleCenter || selectedPresetArea) && (
                      <button
                        onClick={clearSelection}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center text-sm font-medium"
                      >
                        <X size={16} className="mr-1" />
                        Clear Selection
                      </button>
                    )}
                  </div>
                  
                  {/* Status Messages */}
                  {drawingMode === 'circle' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800 font-medium">
                        🔵 Click anywhere on the map to center your search circle
                      </p>
                    </div>
                  )}
                  {drawingMode === 'polygon' && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-purple-800 font-medium">
                          🟣 Click points on the map to draw your area ({searchArea.length} points)
                        </p>
                        {searchArea.length >= 3 && (
                          <button
                            onClick={finishPolygonDrawing}
                            className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                          >
                            Finish
                          </button>
                        )}
                      </div>
                      {searchArea.length < 3 && (
                        <p className="text-xs text-purple-600 mt-1">
                          Need at least 3 points to create an area
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Circle Radius Control */}
                {circleCenter && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <label className="block text-sm font-medium text-blue-900 mb-2">
                      Search Radius: {circleRadius}m
                    </label>
                    <input
                      type="range"
                      min="500"
                      max="5000"
                      step="250"
                      value={circleRadius}
                      onChange={(e) => setCircleRadius(parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>
                )}
              </div>
              
              <div className="h-96 sm:h-[500px]">
                <MapContainer
                  center={chiangMaiCenter}
                  zoom={13}
                  style={{ height: '100%', width: '100%' }}
                  className="z-10"
                  scrollWheelZoom={true}
                  doubleClickZoom={false}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                    url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                  />
                  <MapEvents />
                  
                  {/* Service Areas */}
                  {showServiceAreas && serviceAreas.map(area => (
                    <React.Fragment key={area.id}>
                      {area.polygon ? (
                        <Polygon
                          positions={area.polygon}
                          pathOptions={{
                            color: area.color,
                            fillColor: area.color,
                            fillOpacity: selectedPresetArea === area.id ? 0.4 : 0.1,
                            weight: selectedPresetArea === area.id ? 3 : 1,
                            dashArray: selectedPresetArea === area.id ? undefined : '5, 10'
                          }}
                          eventHandlers={{
                            click: () => selectPresetArea(area.id)
                          }}
                        />
                      ) : area.radius && area.center ? (
                        <Circle
                          center={area.center}
                          radius={area.radius}
                          pathOptions={{
                            color: area.color,
                            fillColor: area.color,
                            fillOpacity: selectedPresetArea === area.id ? 0.4 : 0.1,
                            weight: selectedPresetArea === area.id ? 3 : 1,
                            dashArray: selectedPresetArea === area.id ? undefined : '5, 10'
                          }}
                          eventHandlers={{
                            click: () => selectPresetArea(area.id)
                          }}
                        />
                      ) : null}
                      
                      {area.center && (
                        <Marker position={area.center}>
                          <Popup>
                            <div className="p-2">
                              <div className="flex items-center mb-2">
                                {React.createElement(getAreaTypeIcon(area.type), { size: 16, className: "mr-2", style: { color: area.color } })}
                                <h4 className="font-semibold text-sm">{area.name}</h4>
                              </div>
                              <p className="text-xs text-gray-600 mb-2">{area.description}</p>
                              <button
                                onClick={() => selectPresetArea(area.id)}
                                className="text-xs bg-primary-600 text-white px-2 py-1 rounded hover:bg-primary-700"
                              >
                                Select This Area
                              </button>
                            </div>
                          </Popup>
                        </Marker>
                      )}
                    </React.Fragment>
                  ))}
                  
                  {/* User-drawn polygon - show partial drawing */}
                  {searchArea.length > 0 && (
                    <>
                      {/* Show polygon when 3+ points */}
                      {searchArea.length >= 3 && (
                        <Polygon
                          positions={searchArea}
                          pathOptions={{
                            color: '#3B82F6',
                            fillColor: '#3B82F6',
                            fillOpacity: 0.3,
                            weight: 3
                          }}
                        />
                      )}
                      {/* Show individual markers for each point */}
                      {searchArea.map((point, index) => (
                        <Marker key={index} position={point}>
                          <Popup>Point {index + 1}</Popup>
                        </Marker>
                      ))}
                    </>
                  )}
                  
                  {/* User-drawn circle */}
                  {circleCenter && (
                    <Circle
                      center={circleCenter}
                      radius={circleRadius}
                      pathOptions={{
                        color: '#3B82F6',
                        fillColor: '#3B82F6',
                        fillOpacity: 0.3,
                        weight: 3
                      }}
                    />
                  )}
                </MapContainer>
              </div>
            </div>
          </div>

          {/* Preset Areas & Requirements Form */}
          <div className="lg:col-span-1">
            {!showForm ? (
              <div className="space-y-6">
                {/* Quick Select Areas */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Layers className="mr-2 text-primary-600" size={20} />
                    Quick Select Areas
                  </h4>
                  <div className="space-y-2">
                    {serviceAreas.filter(area => area.type === 'neighborhood').map(area => (
                      <button
                        key={area.id}
                        onClick={() => selectPresetArea(area.id)}
                        className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                      >
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-3 flex-shrink-0"
                            style={{ backgroundColor: area.color }}
                          ></div>
                          <div>
                            <div className="font-medium text-sm text-gray-900">{area.name}</div>
                            <div className="text-xs text-gray-600">{area.description}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-gray-50 rounded-2xl p-6 text-center">
                  <Home className="mx-auto text-gray-400 mb-4" size={48} />
                  <h4 className="text-lg font-semibold text-gray-700 mb-2">
                    Choose Your Search Method
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Select a preset area above, or use the map tools to draw your custom search zone.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <Search className="mr-2 text-primary-600" size={20} />
                  Housing Requirements
                </h4>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Budget Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <DollarSign size={16} className="mr-1 text-primary-600" />
                      Budget Range (THB/month)
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="number"
                        placeholder="Min"
                        value={requirements.budgetMin}
                        onChange={(e) => updateRequirement('budgetMin', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={requirements.budgetMax}
                        onChange={(e) => updateRequirement('budgetMax', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Bedrooms */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <Bed size={16} className="mr-1 text-primary-600" />
                      Number of Bedrooms
                    </label>
                    <select
                      value={requirements.bedrooms}
                      onChange={(e) => updateRequirement('bedrooms', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value={1}>1 Bedroom</option>
                      <option value={2}>2 Bedrooms</option>
                      <option value={3}>3 Bedrooms</option>
                      <option value={4}>4+ Bedrooms</option>
                    </select>
                  </div>

                  {/* Amenities */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Desired Amenities
                    </label>
                    <div className="space-y-3">
                      {[
                        { key: 'hasPool', label: 'Swimming Pool', icon: Waves },
                        { key: 'allowsPets', label: 'Pet-Friendly', icon: Heart },
                        { key: 'hasParking', label: 'Parking Space', icon: Car },
                        { key: 'hasWifi', label: 'WiFi/Internet', icon: Wifi }
                      ].map(({ key, label, icon: Icon }) => (
                        <label key={key} className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={requirements[key as keyof PropertyRequirements] as boolean}
                            onChange={(e) => updateRequirement(key as keyof PropertyRequirements, e.target.checked)}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <Icon size={16} className="ml-2 mr-2 text-gray-600" />
                          <span className="text-sm text-gray-700">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Additional Requirements */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Requirements
                    </label>
                    <textarea
                      value={requirements.additionalRequirements}
                      onChange={(e) => updateRequirement('additionalRequirements', e.target.value)}
                      placeholder="Any other specific requirements or preferences..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitted}
                    className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-green-500 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center"
                  >
                    {isSubmitted ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Opening Email...
                      </>
                    ) : (
                      <>
                        <Search size={18} className="mr-2" />
                        Email Property Request
                      </>
                    )}
                  </button>
                  
                  {/* Alternative contact info */}
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-xs text-gray-600">
                      Email not opening? Contact directly: 
                      <a href="mailto:hi@pattama.co" className="text-primary-600 hover:text-primary-700 font-medium ml-1">
                        hi@pattama.co
                      </a>
                    </p>
                  </div>
                </form>

                {isSubmitted && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 text-sm">
                      Your email client should open with a pre-filled message to hi@pattama.co. 
                      Please send the email to submit your property search request!
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Instructions */}
        <div className="mt-12 bg-blue-50 rounded-2xl p-8">
          <h4 className="text-xl font-semibold text-blue-900 mb-6">How to use the property search:</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold mr-3">
                1
              </div>
              <div>
                <h5 className="font-semibold text-blue-900 mb-1">Quick Select</h5>
                <p className="text-blue-700 text-sm">Choose from popular neighborhoods or service areas</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold mr-3">
                2
              </div>
              <div>
                <h5 className="font-semibold text-blue-900 mb-1">Custom Drawing</h5>
                <p className="text-blue-700 text-sm">Draw a circle or custom area on the map</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold mr-3">
                3
              </div>
              <div>
                <h5 className="font-semibold text-blue-900 mb-1">Set Requirements</h5>
                <p className="text-blue-700 text-sm">Specify budget, bedrooms, and amenities</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold mr-3">
                4
              </div>
              <div>
                <h5 className="font-semibold text-blue-900 mb-1">Get Results</h5>
                <p className="text-blue-700 text-sm">Receive personalized property recommendations</p>
              </div>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-blue-100 rounded-lg">
            <h6 className="font-semibold text-blue-900 mb-2">Area Information:</h6>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-blue-800">
              <div className="flex items-center">
                <Home size={14} className="mr-2" />
                <span>Green/Teal/Purple: Residential neighborhoods</span>
              </div>
              <div className="flex items-center">
                <ShoppingBag size={14} className="mr-2" />
                <span>Red areas: Shopping centers</span>
              </div>
              <div className="flex items-center">
                <GraduationCap size={14} className="mr-2" />
                <span>Orange areas: University district</span>
              </div>
              <div className="flex items-center">
                <Plane size={14} className="mr-2" />
                <span>Gray areas: Airport vicinity</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PropertySearch; 